import React from 'react'
import { useFragment, graphql } from 'react-relay'

import { XStack, YStack } from 'tamagui'

import { Text } from '../Text'
import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'
import { EventCoverPhoto } from '../Event/EventCoverPhoto'
import { PresentationPresenterPhoto } from '../Presentation/PresentationPresenterPhoto'

import type { SearchResultRowNew_searchResult$key } from './__generated__/SearchResultRowNew_searchResult.graphql'

export const SearchResultRow = ({
  searchResult,
}: {
  searchResult: SearchResultRowNew_searchResult$key
}) => {
  const data = useFragment(
    graphql`
      fragment SearchResultRowNew_searchResult on SearchResult {
        entityType
        entity {
          ... on Guild {
            name
            slugId
            memberCount
            ...GuildPrimaryPhotoNew_guild @arguments(imageSize: 48, dpr: 3)
          }
          ... on Event {
            name
            slug
            startAt
            owner {
              ... on Guild {
                name
              }
            }
            ...EventCoverPhotoNew_event @arguments(imageSize: 48, dpr: 3)
          }
          ... on Presentation {
            title
            slug
            presenterFirstName
            presenterLastName
            ...PresentationPresenterPhotoNew_presentation
              @arguments(imageSize: 48, dpr: 3)
          }
        }
      }
    `,
    searchResult
  )

  const { title, subtitle } = getDisplayProps(data)

  return (
    <XStack gap='$3' alignItems='center'>
      <SearchResultPhoto data={data} />
      <YStack flex={1} gap='$1'>
        <Text fontSize='$5' fontWeight='$6' numberOfLines={1}>
          {title}
        </Text>
        <Text fontSize='$4' color='$colorSubtle' numberOfLines={1}>
          {subtitle}
        </Text>
      </YStack>
    </XStack>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SearchResultData = { readonly entityType: string; readonly entity: any }

const SearchResultPhoto = ({ data }: { data: SearchResultData }) => {
  switch (data.entityType) {
    case 'GUILD':
      return <GuildPrimaryPhoto guild={data.entity} size='$12' />
    case 'EVENT':
      return <EventCoverPhoto event={data.entity} size='$12' />
    case 'PRESENTATION':
      return (
        <PresentationPresenterPhoto presentation={data.entity} size='$12' />
      )
    default:
      return null
  }
}

const getDisplayProps = (data: SearchResultData) => {
  const { entityType, entity } = data

  switch (entityType) {
    case 'GUILD': {
      const g = entity as { name: string; memberCount: number }
      return {
        title: g.name,
        subtitle: `Guild${g.memberCount ? ` · ${g.memberCount} members` : ''}`,
      }
    }
    case 'EVENT': {
      const e = entity as {
        name: string
        startAt: string
        owner?: { name?: string }
      }
      const date = e.startAt
        ? new Date(e.startAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })
        : ''
      const parts = ['Event', date, e.owner?.name].filter(Boolean)
      return {
        title: e.name,
        subtitle: parts.join(' · '),
      }
    }
    case 'PRESENTATION': {
      const p = entity as {
        title: string
        presenterFirstName: string
        presenterLastName: string
      }
      const presenter = [p.presenterFirstName, p.presenterLastName]
        .filter(Boolean)
        .join(' ')
      return {
        title: p.title,
        subtitle: `Presentation${presenter ? ` · ${presenter}` : ''}`,
      }
    }
    default:
      return { title: '', subtitle: '' }
  }
}
